import { myPerms } from '@yc/api-client'
import type { PermNode } from '@yc/api-client'
import { readonly, ref } from 'vue'

const codes = ref<ReadonlySet<string>>(new Set<string>())
const menus = ref<PermNode[]>([])
const loading = ref(false)
const loadedFor = ref('')

type Pending = { el: HTMLElement; value: string | string[] }
const pendingEls = new Set<Pending>()

function toList(value: string | string[] | undefined): string[] {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

export function applyPerm(el: HTMLElement, value: string | string[] | undefined): void {
  const list = toList(value)
  if (list.length === 0) return
  pendingEls.add({ el, value: list })
  evaluate({ el, value: list })
}

function evaluate(p: Pending): void {
  // 权限尚未加载时不能判定：此时 codes 为空会把所有受控元素误删，故挂起等 load() 统一评估
  if (loadedFor.value === '') return
  const list = toList(p.value)
  if (can(...list)) {
    pendingEls.delete(p)
    return
  }
  p.el.remove()
  pendingEls.delete(p)
}

function reevaluateAll(): void {
  for (const p of [...pendingEls]) evaluate(p)
}

/** 写入权限码并立即评估所有已挂起的受控元素（load 与本地缓存恢复共用）。 */
export function applyCodes(list: string[], app = 'portal'): void {
  codes.value = new Set(list)
  loadedFor.value = app
  reevaluateAll()
}

export function resetPerms(): void {
  codes.value = new Set<string>()
  menus.value = []
  loadedFor.value = ''
  reevaluateAll()
}

export function can(...list: Array<string | undefined>): boolean {
  if (list.length === 0) return true
  return list.some((c) => !!c && codes.value.has(c))
}

export function usePermissions() {
  async function load(app: string, force = false): Promise<void> {
    if (!force && loadedFor.value === app) return
    loading.value = true
    try {
      const r = await myPerms(app)
      menus.value = r.menus
      applyCodes(r.codes, app)
    } finally {
      loading.value = false
    }
  }

  return {
    codes: readonly(codes),
    menus: readonly(menus),
    loading: readonly(loading),
    can,
    load,
    reset: resetPerms,
  }
}

export const permState = { codes, menus, loadedFor }